import React, { useContext } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { FamilyEnrollmentContext } from 'features/family-enrollment/context';
import { BaseStep } from '../BaseStep';
import { useBaseStyles, ButtonControls } from 'features/family-enrollment/steps/utils';
import {
    FormikTextField,
    FormikTextFieldSecured,
    FormikCheckboxField,
} from 'components/fields/FormikTextField';
import {
    orgEnrollPasswordValidator,
    confirmPasswordValidator,
} from 'common/modules/form-validation/validators';
import { FieldSpacer } from 'components/fields/FieldSpacer';
import { FormikSetPasswordField } from 'components/fields/FormikSetPasswordField';
import { useAppConfig } from 'common/features/app-config/utils/useAppConfig';
import { UserContent } from 'components/user-content/UserContent';
import { useContentfulGlobalContent } from 'common/api/contentful/global';
import { RegistrationRequest } from 'common/api/auth/models/RegistrationRequest';
import { link } from 'styles/typography';
import { Branding, IS_FLIPGIVE } from 'common/env';
import { ExternalLinks } from 'externalLinks';
import { logParticipantEnrollment } from 'config/analytics';
import { createStyles } from 'utils/createStyle';
import { ContentfulGlobal } from 'common/api/contentful/models/ContentfulGlobal';

const marketingOptInStyles = createStyles({
    wrapper: [
        '',
        {
            '& div.flex.flex-row': { alignItems: 'flex-start' },
        },
    ],
});

const MARKETING_OPT_IN_COPY =
    "Yes, I'd like to receive emails from FlipGive so I don't miss out on bonus earnings, online shopping deals, and new brand updates. I understand I can unsubscribe at any time.";
const FLIPGIVE_SUPPORT_PHONE = '800-727-4715';
const FLIPGIVE_SUPPORT_PHONE_OPTION = 'Option 3';
const FLIPGIVE_SUPPORT_EMAIL = 'support@flipgive.com';

interface AccountDetailsFormValues {
    email: string;
    password: string;
    confirmPassword: string;
    marketingOptIn: boolean;
}

const PasswordField = ({
    minAcceptableScore,
    globalContent,
}: {
    minAcceptableScore: number | null;
    globalContent: ContentfulGlobal | null;
}) => {
    return (
        <div>
            <FormikSetPasswordField
                name="password"
                label="Password (8+ characters)"
                maxLength={200}
                autoComplete="new-password"
                validate={orgEnrollPasswordValidator}
                minAcceptableScore={minAcceptableScore}
            />
            {globalContent && (
                <div className="text-xs text-grey-1 mt-3">
                    <UserContent content={globalContent?.setPasswordTooltip} />
                </div>
            )}
        </div>
    );
};

export const AccountDetailsStep = () => {
    const {
        goToNextStep,
        updateUserAccountInfo,
        userAccountInfo,
        organization,
        isEnrollmentTestVariant,
        isSocialRegistrationEnabled,
        stepHeaders,
    } = useContext(FamilyEnrollmentContext);
    const { appConfig } = useAppConfig();
    const baseStyles = useBaseStyles();
    const globalContent = useContentfulGlobalContent();

    const isCanadianUser = organization?.countryCode === 'CA' || IS_FLIPGIVE;
    const showMarketingOptIn = !isSocialRegistrationEnabled && isCanadianUser;

    const initialValues: AccountDetailsFormValues = {
        email: userAccountInfo?.email || '',
        password: userAccountInfo?.password || '',
        confirmPassword: userAccountInfo?.confirmPassword || '',
        marketingOptIn: showMarketingOptIn ? userAccountInfo?.marketingOptIn ?? false : false,
    };

    const onSubmit = async (values: AccountDetailsFormValues) => {
        updateUserAccountInfo((currentState: RegistrationRequest) => ({
            ...currentState,
            password: values.password,
            confirmPassword: values.confirmPassword,
            ...(!isSocialRegistrationEnabled && {
                marketingOptIn: showMarketingOptIn ? values.marketingOptIn : false,
            }),
        }));

        logParticipantEnrollment(
            isEnrollmentTestVariant ? 5 : 4,
            'account_details',
            {
                orgId: organization?.id,
                programType: organization?.type,
                flowVariant: isEnrollmentTestVariant ? 'variant' : 'control',
            },
            {
                experiment_name: 'participant_enrollment_test_web',
                experiment_variant: isEnrollmentTestVariant ? 'variant' : 'control',
            }
        );
        goToNextStep();
    };

    const heading = isSocialRegistrationEnabled
        ? 'Create Password'
        : stepHeaders?.signInInfoHeading || 'Account Details';

    return (
        <BaseStep
            contentClassName="text-md sm:text-lg font-thin"
            heading={heading}
            subheading={null}
        >
            <div className={baseStyles.formContainer}>
                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    {(formikProps: FormikProps<AccountDetailsFormValues>): React.ReactElement => (
                        <Form>
                            {!isSocialRegistrationEnabled && (
                                <>
                                    <FormikTextField
                                        type="email"
                                        name="email"
                                        label="Email"
                                        disabled={true}
                                    />
                                    <FieldSpacer />
                                </>
                            )}
                            <PasswordField
                                minAcceptableScore={appConfig.passwordPolicy.minimumZxcvbnScore}
                                globalContent={globalContent}
                            />
                            <FieldSpacer />
                            <FormikTextFieldSecured
                                name="confirmPassword"
                                label="Confirm Password"
                                maxLength={200}
                                autoComplete="new-password"
                                validate={confirmPasswordValidator(formikProps.values.password)}
                            />
                            {showMarketingOptIn && (
                                <>
                                    <FieldSpacer />
                                    <div className={marketingOptInStyles.wrapper}>
                                        <FormikCheckboxField
                                            name="marketingOptIn"
                                            id="marketingOptIn"
                                            label={MARKETING_OPT_IN_COPY}
                                            className="text-grey-1 text-sm font-semibold pl-1 leading-4"
                                            checked={formikProps.values.marketingOptIn}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="mt-6 text-xs text-grey-2">
                                By proceeding, I acknowledge I have read and agree to the {Branding}{' '}
                                <a
                                    href={ExternalLinks.ParticipantTermsOfUse}
                                    target="_blank"
                                    rel="noreferrer"
                                    className={link}
                                >
                                    Participant Terms of Use
                                </a>
                                .
                            </div>
                            {showMarketingOptIn && IS_FLIPGIVE && (
                                <div className="mt-2 text-xs text-grey-2">
                                    <a href={`tel:${FLIPGIVE_SUPPORT_PHONE}`} className={link}>
                                        {FLIPGIVE_SUPPORT_PHONE}
                                    </a>{' '}
                                    ({FLIPGIVE_SUPPORT_PHONE_OPTION}) |{' '}
                                    <a href={`mailto:${FLIPGIVE_SUPPORT_EMAIL}`} className={link}>
                                        {FLIPGIVE_SUPPORT_EMAIL}
                                    </a>
                                </div>
                            )}
                            <ButtonControls />
                        </Form>
                    )}
                </Formik>
            </div>
        </BaseStep>
    );
};
